import { StatusBar, Modal, PrimaryButton, SecondaryButton, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './SyncReportModal.module.scss';
import chefPlating from 'src/assets/images/chef-plating.png';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { commitSync } from 'src/api/commitSync/commitSync';
import { useQueryClient } from '@tanstack/react-query';
import { SyncModalHeaders } from './SyncModalHeaders/SyncModalHeaders';
import { SyncModalItemSubHeaders } from './SyncModalItemSubHeaders/SyncModalItemSubHeaders';
import { SyncProductList } from './SyncProductList/SyncProductList';
import { SyncAcknowledgeChangeCheckBox } from './SyncAcknowledgeChangeCheckBox/SyncAcknowledgeChangeCheckBox';
import { IProductOverrides } from 'src/api/commitSync/types';
import { EProductLibraryStatus, ISyncProduct } from '../../api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { IProductLibraryStatus } from '../../hooks/useProductLibraryStatus/useProductLibraryStatus';
import { QueryKeys, SYNC_STATUS_QUERY } from '../../hooks/QueryKeys';
import { useEvents } from '../../hooks/useEvents';
import { NotificationStatus } from 'src/api/getEvents';
import { ItemType } from '@weareopenr/types';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { NotificationType } from 'src/api/getNotifications/getNotifications';

export const productDataKeys: string[] = ['inserts', 'updates', 'deletes'];
export const pageTitles = ['New Products', 'Updated Products', 'Removed Products'];

interface ISyncDiff {
  inserts: ISyncProduct[];
  updates: ISyncProduct[];
  deletes: ISyncProduct[];
}

interface ISyncReportModalProps {
  isOpen: boolean;
  products?: ISyncDiff;
  closeModal: () => void;
  rejectSync: () => void;
}

interface AdditionalProps extends Omit<ISyncReportModalProps, 'isOpen'> {
  stepCount: number;
  setStepCount: Dispatch<SetStateAction<number>>;
  hasScheduledEvent: boolean;
}

const SCHEDULED_EVENT_NOTE = (
  <span>
    <strong>Quick note</strong> - You have upcoming schedules. New products will need a price, POS ID, and tax rate
    before they can be published. Please ensure all data is present to prevent publish failure.
  </span>
);

const Content = ({ products, closeModal, rejectSync, stepCount, setStepCount, hasScheduledEvent }: AdditionalProps) => {
  const queryClient = useQueryClient();
  const [isChangeAccepted, setIsChangeAccepted] = useState<boolean>(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { markNotificationRead } = useUpdateNotification();

  useEffect(() => {
    setIsChangeAccepted(false);
  }, [stepCount]);

  const productKey: string = productDataKeys[stepCount];
  const productsListByKeyName: ISyncProduct[] = products[productKey];
  const showModifierCheckbox: boolean = productKey === productDataKeys[0];

  const acceptChanges = () => {
    setIsChangeAccepted((prev) => !prev);
  };

  const reject = () => {
    rejectSync();
    closeModal();
  };
  const stepNext = () => {
    if (stepCount !== 2) {
      setStepCount(stepCount + 1);
    }
  };

  const commitSyncSubmit = async (selectedProductIds: string[]) => {
    clearError();
    setIsSubmitting(true);
    try {
      const formattedPayload: IProductOverrides[] = selectedProductIds.map((itemId: string) => ({
        item_id: itemId,
        type: ItemType.MODIFIER,
      }));

      await commitSync(formattedPayload);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.priceSchemes] });
      // set sync status to PENDING
      queryClient.setQueryData(SYNC_STATUS_QUERY, (old: IProductLibraryStatus) => ({
        ...old,
        sync_status: EProductLibraryStatus.PENDING,
      }));

      markNotificationRead((notification) => notification.type === NotificationType.PRODUCT_SYNC);

      closeModal();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'An error has occurred; sync changes could not be accepted.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckedProduct = (event, productId) => {
    if (event.currentTarget.checked) {
      if (!selectedProductIds.includes(productId)) {
        setSelectedProductIds([...selectedProductIds, productId]);
      }
    } else {
      setSelectedProductIds(selectedProductIds.filter((itemProductId) => itemProductId !== productId));
    }
  };

  return (
    <div>
      <SyncModalHeaders stepCount={stepCount} pageTitle={pageTitles[stepCount]} />
      <SyncModalItemSubHeaders showCheckbox={showModifierCheckbox} />
      <SyncProductList
        productListData={productsListByKeyName}
        handleCheckedProduct={handleCheckedProduct}
        selectedProducts={selectedProductIds}
        showCheckbox={showModifierCheckbox}
        stepCount={stepCount}
      />
      <SyncAcknowledgeChangeCheckBox
        acknowledgeChange={isChangeAccepted}
        acceptChanges={acceptChanges}
        productsCount={productsListByKeyName.length}
        stepCount={stepCount}
      />

      {hasScheduledEvent && stepCount === 0 && <StatusBar message={SCHEDULED_EVENT_NOTE} variant="info" />}
      <ButtonContainer
        left={
          <SecondaryButton data-testid="reject-button" buttonStyle="outline" onClick={reject} size="full-width">
            Reject Sync
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            onClick={stepCount < 2 ? stepNext : () => commitSyncSubmit(selectedProductIds)}
            size="full-width"
            disabled={!isChangeAccepted || isSubmitting}
            data-testid="next-accept-button"
            loading={isSubmitting}
          >
            {stepCount < 2 ? 'Next' : 'Accept Sync'}
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </div>
  );
};

export const SyncReportModal = ({ isOpen, products, closeModal, rejectSync }: ISyncReportModalProps) => {
  const [stepCount, setStepCount] = useState<number>(0);
  const { data: scheduledEvents } = useEvents({ status: NotificationStatus.SCHEDULED }, { enabled: isOpen });
  const hasScheduledEvent = !!scheduledEvents?.data?.length;

  useEffect(() => {
    if (!isOpen) {
      setStepCount(0);
    }
  }, [isOpen]);

  const stepBack = () => {
    if (stepCount > 0) {
      setStepCount(stepCount - 1);
    }
  };

  return (
    <Modal
      open={isOpen && !!products}
      closeModal={closeModal}
      image={chefPlating}
      imageAlt="chef"
      onBack={stepCount > 0 ? stepBack : undefined}
    >
      <Content
        products={products}
        closeModal={closeModal}
        rejectSync={rejectSync}
        stepCount={stepCount}
        setStepCount={setStepCount}
        hasScheduledEvent={hasScheduledEvent}
      />
    </Modal>
  );
};
